import type { Attachment, Message, Participant, Room } from "@catmail/shared";
import { ArrowDown, Mail } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type UIEvent,
} from "react";

import { Skeleton } from "@/components";
import { FOCUS_ON_DARK, PRESS } from "@/lib/interaction";
import { RISE_IN } from "@/lib/motion";

import { buildTimeline, isContinuation } from "../../lib/timeline";
import { MessageBubble } from "../MessageBubble";

type ChatTimelineProps = {
  room: Room;
  messages: Message[];
  /** 맨 위에 한 줄로 붙는 방 안내 — "이 채팅방은 알림이 꺼져 있어요" */
  notice?: string | null;
  /** 아직 첫 페이지가 오지 않았다. 빈 대화와 다르게 보여야 한다 */
  loading?: boolean;
  onOpenHtml?: (message: Message) => void;
  onOpenAttachment?: (attachment: Attachment) => void;
  /** 아바타나 보낸사람 이름을 눌렀을 때 */
  onOpenProfile?: (sender: Participant) => void;
  /** 못 보낸 메일을 다시 큐에 넣는다 */
  onRetry?: (messageId: string) => void;
  /** 위로 스크롤해 과거를 더 불러온다 */
  onLoadOlder?: () => void;
  hasOlder?: boolean;
  loadingOlder?: boolean;
};

/** 이만큼 남았을 때 미리 불러온다. 꼭대기에 닿고 나서 부르면 한 번 덜컥한다. */
const LOAD_OLDER_THRESHOLD_PX = 160;

/** 바닥에서 이 안쪽이면 "끝을 보고 있다"로 친다. */
const NEAR_BOTTOM_PX = 140;

/**
 * 대화 타임라인.
 *
 * 여기는 일부러 가상화하지 않는다. 방 하나에 들고 있는 메시지 수는 위로
 * 스크롤할 때 더 불러오는 방식으로 묶어둘 것이고, 가상화 라이브러리는 짧은
 * 대화에서 스크롤 위치를 제멋대로 잡아 화면이 비어 보이는 문제가 있었다.
 * 목록이 길어지는 쪽(방 목록)만 가상화한다.
 */
export function ChatTimeline({
  room,
  messages,
  notice,
  loading = false,
  onOpenHtml,
  onOpenAttachment,
  onOpenProfile,
  onRetry,
  onLoadOlder,
  hasOlder = false,
  loadingOlder = false,
}: ChatTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  /** 과거를 불러오기 직전의 scrollHeight. 렌더 후 위치를 되돌리는 데 쓴다. */
  const heightBeforePrepend = useRef<number | null>(null);
  /**
   * 새 메시지를 따라 내려갈 것인가.
   *
   * 무조건 따라가면 **옛날 대화를 읽는 도중에 메일 한 통이 오는 순간 맨 아래로
   * 튕긴다.** 읽던 자리를 잃고 다시 찾아 올라가야 한다. 끝을 보고 있을 때만
   * 따라가는 것이 메신저의 관습이고, 그때만 새 말풍선이 시야에 들어온다.
   */
  const stickToBottom = useRef(true);

  /**
   * 위에서 읽는 동안 아래에 새 메일이 쌓였는가.
   *
   * 따라 내려가지 않기로 한 이상 **왔다는 사실은 알려야 한다.** 알리지 않으면
   * 조용히 안 온 것과 구분되지 않아서, 튕기지 않는 대신 놓치게 된다.
   */
  const [unseenBelow, setUnseenBelow] = useState(false);

  // 조용한 방은 말풍선이 제목을 이고 있으므로 제목 줄을 따로 넣지 않는다.
  const items = useMemo(
    () => buildTimeline(messages, notice, room.isQuiet),
    [messages, notice, room.isQuiet],
  );

  /**
   * 떠오르게 할 말풍선을 골라둔다.
   *
   * 방을 여는 순간 이미 있던 것까지 전부 떠오르면, 대화를 읽는 게 아니라 화면이
   * 요동치는 것으로 보인다. **열어둔 채로 새로 도착한 것**만 고른다. 위로
   * 스크롤해 불러온 과거도 빠진다 — 그건 새 소식이 아니라 원래 있던 것이다.
   *
   * 기준(`newestAt`)은 첫 페이지가 **다 온 뒤에** 잡는다. 오기 전에 잡으면
   * 뒤늦게 도착한 예전 메일이 전부 새 것으로 보인다.
   */
  const entrance = useRef({
    roomId: "",
    primed: false,
    newestAt: "",
    ids: new Set<string>(),
  });

  if (entrance.current.roomId !== room.id) {
    entrance.current = {
      roomId: room.id,
      primed: false,
      newestAt: "",
      ids: new Set(),
    };
  }

  const entering = entrance.current;
  if (!entering.primed) {
    if (!loading) {
      entering.primed = true;
      entering.newestAt = messages.at(-1)?.sentAt ?? "";
    }
  } else {
    // 시간순이라 앞에서부터 훑으면 기준이 단조롭게 올라간다.
    for (const message of messages) {
      if (message.sentAt > entering.newestAt) {
        entering.newestAt = message.sentAt;
        /*
         * **내가 보낸 것은 떠오르지 않는다.**
         *
         * 도착을 알리는 움직임이라 내가 방금 친 말에는 알릴 것이 없고, 무엇보다
         * 두 번 돈다 — 누른 즉시 올리는 낙관적 말풍선과 서버가 준 진짜 말풍선은
         * id가 달라서 각각 한 번씩 등장한다. 같은 자리에서 같은 애니메이션이
         * 연달아 도는 것이 "뚝뚝 올라간다"의 정체였다.
         */
        if (message.direction === "incoming") entering.ids.add(message.id);
      }
    }
  }

  const senderById = useMemo(() => {
    const map = new Map<string, Participant>();
    for (const participant of room.participants) {
      map.set(participant.id, participant);
    }
    return map;
  }, [room.participants]);

  // 마지막 항목의 식별자. 새 메시지가 "끝에" 붙었는지 판단하는 기준이다.
  const last = items.at(-1);
  const lastKey = last?.kind === "message" ? last.message.id : (last?.at ?? "");
  /** 방금 내가 보낸 것. 어디를 보고 있었든 내 말은 보여야 한다. */
  const lastIsMine = last?.kind === "message" && last.message.direction === "outgoing";

  // 방을 바꾸면 곧바로 맨 아래로. 애니메이션 없이 붙여야 깜빡이지 않는다.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (element) element.scrollTop = element.scrollHeight;
    stickToBottom.current = true;
    setUnseenBelow(false);
  }, [room.id]);

  // 과거를 앞에 끼워 넣으면 보고 있던 내용이 통째로 아래로 밀린다.
  // 늘어난 높이만큼 scrollTop을 더해 읽던 자리를 그대로 붙잡는다.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    const before = heightBeforePrepend.current;
    if (element && before !== null) {
      element.scrollTop += element.scrollHeight - before;
      heightBeforePrepend.current = null;
    }
  }, [items.length]);

  // 새 메시지가 왔을 때만 따라 내려간다. 길이를 기준으로 하면 과거를 불러왔을
  // 때도 맨 아래로 튕겨서, 읽으려던 옛날 대화를 놓친다.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    // 위로 올라가 읽는 중이면 가만히 둔다. 내가 보낸 것만 예외다.
    if (!stickToBottom.current && !lastIsMine) {
      setUnseenBelow(true);
      return;
    }

    stickToBottom.current = true;
    setUnseenBelow(false);
    /*
     * 내 말은 **부드럽게 굴리지 않는다.**
     *
     * 보낼 때는 이미 끝을 보고 있어서 굴릴 거리가 한 줄뿐인데, 그 한 줄을
     * 300ms에 걸쳐 굴리는 동안 낙관적 말풍선이 진짜 말풍선으로 갈리며 스크롤이
     * 한 번 더 걸린다. 굴리는 도중에 다시 굴리면 화면이 덜컥인다.
     */
    element.scrollTo({
      top: element.scrollHeight,
      behavior: lastIsMine ? "auto" : "smooth",
    });
  }, [lastKey, lastIsMine]);

  function jumpToLatest() {
    const element = scrollRef.current;
    if (!element) return;

    stickToBottom.current = true;
    setUnseenBelow(false);
    element.scrollTo({ top: element.scrollHeight, behavior: "smooth" });
  }

  function handleScroll(event: UIEvent<HTMLDivElement>) {
    const element = event.currentTarget;

    const atBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight <
      NEAR_BOTTOM_PX;

    stickToBottom.current = atBottom;
    // 직접 내려와 끝을 봤으면 알림은 할 일을 다 했다.
    if (atBottom) setUnseenBelow(false);

    if (element.scrollTop > LOAD_OLDER_THRESHOLD_PX) return;
    // 이미 불러오는 중이면 겹쳐 부르지 않는다.
    if (!hasOlder || loadingOlder || heightBeforePrepend.current !== null) return;

    heightBeforePrepend.current = element.scrollHeight;
    onLoadOlder?.();
  }

  // 다 오기 전에 "없다"고 말하지 않는다. 올 것이 있다는 것과 아무것도 없다는
  // 것은 다른 말이고, 사용자에게는 다르게 보여야 한다.
  if (loading) return <TimelineLoading />;

  if (items.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center text-sm text-muted-2">
        아직 주고받은 메일이 없어요
      </div>
    );
  }

  return (
    // 알림 단추를 대화 위에 띄우려면 자리를 잡아줄 상자가 하나 있어야 한다.
    <div className="relative flex min-h-0 flex-1 flex-col">
      {unseenBelow ? (
        <button
          type="button"
          onClick={jumpToLatest}
          className={`bg-ink text-on-ink absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-1.5 rounded-full py-2 pr-4 pl-3.5 text-[13px] font-semibold shadow-modal ${RISE_IN} ${PRESS} ${FOCUS_ON_DARK}`}
        >
          <ArrowDown className="size-3.5" />새 메시지
        </button>
      ) : null}

      {/* min-h-0가 없으면 flex 자식이 내용만큼 부풀어 스크롤이 생기지 않는다. */}
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="min-h-0 flex-1 overflow-y-auto"
    >
      {/* justify-end — 짧은 대화는 카카오톡처럼 아래에서부터 쌓인다 */}
      <div className="flex min-h-full flex-col justify-end gap-4 px-4 py-5 sm:px-8 sm:py-6.5">
        {hasOlder ? (
          <p className="py-2 text-center text-xs text-muted-2">
            {loadingOlder ? "이전 대화를 불러오는 중…" : "위로 올리면 더 볼 수 있어요"}
          </p>
        ) : null}

        {items.map((item, index) => {
          switch (item.kind) {
            case "dateChip":
              return <CenterChip key={`date-${item.at}`}>{item.label}</CenterChip>;

            case "notice":
              return <CenterChip key={`notice-${index}`}>{item.text}</CenterChip>;

            case "subjectShift":
              return (
                <div
                  key={`shift-${item.at}-${index}`}
                  className="flex items-center gap-3 py-2"
                >
                  <span className="h-px flex-1 bg-line-strong" />
                  <span className="flex min-w-0 shrink items-center gap-1.5 text-xs text-muted-2">
                    <Mail className="size-3 shrink-0 text-muted-3" />
                    {item.first ? null : (
                      <span className="shrink-0 text-muted-3">다른 이야기</span>
                    )}
                    <span className="truncate">{item.subject}</span>
                  </span>
                  <span className="h-px flex-1 bg-line-strong" />
                </div>
              );

            case "message":
              return (
                <MessageBubble
                  key={item.message.id}
                  message={item.message}
                  sender={senderById.get(item.message.senderId) ?? null}
                  entering={entering.ids.has(item.message.id)}
                  continuation={isContinuation(items, index)}
                  /*
                    1:1에도 이름을 올린다.

                    예전에는 여러 명 방에서만 올렸다 — 헤더가 이미 누구인지
                    말하니 말풍선마다 또 얹을 것 없다고 봤다. 그런데 실제로 쓰면
                    받은 말풍선 위가 휑하다. 메일은 같은 방 안에서도 보낸 주소가
                    갈릴 수 있고(참조로 받은 것, 같은 사람의 다른 주소), 이름이
                    없으면 아바타 하나로 그걸 가려내야 한다.

                    "이름만 세로로 늘어선다"는 걱정은 `continuation`이 막는다 —
                    같은 사람이 잇달아 보낸 것에는 안 붙는다.
                  */
                  showSenderName
                  quiet={room.isQuiet}
                  onOpenHtml={onOpenHtml}
                  onOpenAttachment={onOpenAttachment}
                  onOpenProfile={onOpenProfile}
                  onRetry={onRetry}
                />
              );
          }
        })}
      </div>
    </div>
    </div>
  );
}

function CenterChip({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-center">
      <span className="rounded-full bg-chip px-3 py-1.25 text-xs text-muted-2">
        {children}
      </span>
    </div>
  );
}

/** 곧 올 대화의 자리. 실제 말풍선처럼 좌우로 번갈아 놓아야 대화로 읽힌다. */
function TimelineLoading() {
  return (
    <div className="flex min-h-0 flex-1 flex-col justify-end gap-4 px-4 py-5 sm:px-8 sm:py-6.5">
      {[
        { mine: false, width: "w-64" },
        { mine: true, width: "w-40" },
        { mine: false, width: "w-52" },
        { mine: true, width: "w-56" },
      ].map((row, index) => (
        <div key={index} className={row.mine ? "flex justify-end" : "flex gap-2.5"}>
          {row.mine ? null : <Skeleton className="size-9 shrink-0 rounded-avatar-sm" />}
          <Skeleton className={`h-11 ${row.width} rounded-bubble`} />
        </div>
      ))}
    </div>
  );
}
